const { debounce, getElement, getElements } = window.BAO.Utils

class CollectionFiltersRenderer {
  /**
   * @param {CollectionFiltersForm} form
   */
  constructor (form) {
    /**
     * @type {CollectionFiltersForm}
     * @public
     */
    this.form = form

    /**
     * @type {CollectionFiltersPaginationRender}
     * @public
     */
    this.paginationRenderer = new CollectionFiltersPaginationRender(this)

    /**
     * @type {CollectionPromoHandler}
     * @public
     */
    this.promoHandler = new CollectionPromoHandler(this)
  }

  /**
   * @param {Function} filterDataUrl
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  fromCache (filterDataUrl, e) {
    const html = this.form.filterData.find(element => filterDataUrl(element))
      .html

    this.form.log('fromCache', { filterDataUrl, e, html })

    this.render(html, e)
  }

  /**
   * @param {String} section
   * @param {String} url
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  async fromFetch (section, url, e) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.form.log('fromFetch', { section, url, html })

    this.form.filterData.push({ html, url })

    this.render(html, e)
  }

  /**
   * @param {String} html
   * @param {SubmitEvent|PointerEvent} e
   *
   * @returns {void}
   */
  render (html, e) {
    this.renderFilters(html, e)
    this.renderProductGridContainer(html, e)
    this.paginationRenderer.render(html)

    this.form.loading = false
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  renderFilters (html, e) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const detailSelector = [
      this.form.els?.facet?.selector,
      this.form.externalEls?.facet?.selector,
    ]
      .filter(Boolean)
      .join(',')

    if (!detailSelector) return

    const facetDetailElements = getElements(detailSelector, {
      context: parsedHTML,
    })
    /**
     * @param {HTMLElement} element
     *
     * @returns {Boolean}
     */
    const matchesIndex = element => {
      const facet = e ? e.target.closest(detailSelector) : null

      return facet ? element.dataset.index === facet.dataset.index : false
    }
    const facetsToRender = facetDetailElements.filter(el => !matchesIndex(el))
    const countsToRender = facetDetailElements.find(el => matchesIndex(el))

    this.form.log('renderFilters', {
      e,
      parsedHTML,
      facetDetailElements,
      facetsToRender,
      countsToRender,
    })

    facetsToRender.forEach(element => {
      const selector = detailSelector
        .split(',')
        .map(selector => `${selector}[data-index="${element.dataset.index}"]`)
        .join(',')

      const el = getElement(selector)

      el.innerHTML = element.innerHTML
    })

    this.renderAdditionalElements(parsedHTML)

    if (countsToRender) {
      this.renderSelectedFacetCount(
        countsToRender,
        e.target.closest(detailSelector),
      )
    }

    this.updateTotal()
  }

  updateTotal () {
    const totalCounters = getElements(
      '[data-collection-filters-form-el="total"]',
    )
    const activeFilters = this.form.querySelectorAll(
      'input[type="checkbox"]:checked, [data-price-range-input]:not([value=""])',
    )
    const totalActive = activeFilters.length

    totalCounters.forEach(counter => {
      counter.textContent = `(${totalActive})`
    })
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderAdditionalElements (parsedHTML) {
    this.promoHandler.setTotalItemCount()
  }

  /**
   * @param {String} html
   * @param {PointerEvent} e
   *
   * @returns {void}
   */
  renderProductGridContainer (html, e) {
    const direction = this.getPaginationDirection(e)
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const newItems = document.createDocumentFragment()
    const newElements = getElements(
      '[data-collection-filters-form-external-el="item"]',
      {
        context: parsedHTML,
      },
    )

    if (newElements.length > 0) {
      this.form.externalEls.empty.element.innerHTML = ''
      newElements.forEach(el => newItems.appendChild(el))

      if (direction == null) {
        this.form.externalEls['item-container'].element.innerHTML = ''
        this.form.externalEls['item-container'].element.append(newItems)
      } else if (direction === 'PREVIOUS') {
        this.form.externalEls['item-container'].element.prepend(newItems)
      } else if (direction === 'NEXT') {
        this.form.externalEls['item-container'].element.append(newItems)
      }

      const newGalleries = getElements('product-card-gallery')
      newGalleries.forEach(gallery => {
        if (gallery.reinitialise) {
          gallery.reinitialise()
        }
      })

      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent,
      )
      if (!isSafari) return

      const images = getElements('img', {
        context: this.form.externalEls['item-container'].element,
      })

      images.forEach(image => {
        // eslint-disable-next-line no-self-assign
        image.outerHTML = image.outerHTML
      })
    } else {
      const emptyHTML = getElement(this.form.externalEls.empty.selector, {
        context: parsedHTML,
      })

      this.form.externalEls['item-container'].element.innerHTML = ''
      this.form.externalEls.empty.element.innerHTML = emptyHTML.innerHTML
    }
  }

  /**
   * @param {HTMLElement} source
   * @param {HTMLElement} target
   *
   * @returns {void}
   */
  renderSelectedFacetCount (source, target) {
    const sourceElement = getElement(
      this.form.els['facets-selected'].selector,
      { context: source },
    )
    const targetElement = getElement(
      this.form.els['facets-selected'].selector,
      { context: target },
    )

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML
    }
  }

  /**
   * @param {PointerEvent} e
   *
   * @return 'NEXT'|'PREVIOUS'|null
   */
  getPaginationDirection (e) {
    if (typeof e === 'undefined') return null

    const paginationElement = e.target.closest(
      `${this.form.externalEls['load-previous'].selector}, ${this.form.externalEls['load-more'].selector}`,
    )
    if (!paginationElement) return null

    return paginationElement === this.form.externalEls['load-previous']?.element
      ? 'PREVIOUS'
      : 'NEXT'
  }

  /**
   * @returns {Element[]}
   */
  get countElements () {
    return getElements('[data-product-grid-count]')
  }

  /**
   * @returns {String[]}
   */
  get activeFacetSelectors () {
    if ('active-facets' in this.form.externalEls) {
      return [
        this.form.els['active-facets'].selector,
        this.form.externalEls['active-facets'].selector,
      ]
    }

    return [this.form.els['active-facets'].selector]
  }
}

class CollectionPromoHandler {
  constructor (renderer) {
    /**
     * @type {CollectionFiltersForm}
     * @public
     */
    this.renderer = renderer

    this.initialise()
  }

  initialise () {
    this.setTotalItemCount()
    this.setupListeners()
  }

  setupListeners () {
    if (!this.renderer.form) return
    this.renderer.form.listeners.add(
      this.renderer.form,
      'AFTER_RESIZE',
      this.setTotalItemCount.bind(this),
    )
  }

  setTotalItemCount () {
    const itemsContainerEl = this.renderer.form.externalEls?.['item-container']
      ?.element
    const itemEls = this.renderer.form.externalEls?.item?.elements || []
    const promoItemEls =
      this.renderer.form.externalEls?.['promo-item']?.elements || []
    if (!itemsContainerEl) return

    const totalItemCount = promoItemEls.length + itemEls.length
    const columnCount = Number(
      getComputedStyle(itemsContainerEl)
        .getPropertyValue('--ColumnCount')
        .trim(),
    )
    const lastRowItemCount = totalItemCount % columnCount

    this.isLastPage = !this.renderer.paginationRenderer.shouldShowLoadMore
    if (this.isLastPage) {
      itemsContainerEl.removeAttribute('data-last-row-item-count')
    } else {
      itemsContainerEl.setAttribute(
        'data-last-row-item-count',
        lastRowItemCount,
      )
    }
  }
}

class CollectionFiltersPaginationRender {
  constructor (renderer) {
    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = renderer
  }

  /**
   * @param {string} html
   */
  render (html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    this.renderLoadPrevious(parsedHTML)
    this.renderLoadMore(parsedHTML)
  }

  /**
   * @param {Document} html
   */
  renderLoadPrevious (html) {
    if (!this.shouldShowLoadPrevious) {
      return (this.form.externalEls['load-previous'].element.innerHTML = '')
    }
    if (!this.shouldRerenderLoadPrevious) return

    return this._renderEl('load-previous', html)
  }

  /**
   * @param {Document} html
   */
  renderLoadMore (html) {
    if (!this.shouldShowLoadMore) {
      return (this.form.externalEls['load-more'].element.innerHTML = '')
    }
    if (!this.shouldRerenderLoadMore) return

    return this._renderEl('load-more', html)
  }

  /**
   * @param {string} section
   * @param {Document} html
   */
  _renderEl (section, html) {
    this.form.externalEls[section].element.innerHTML = getElement(
      this.form.externalEls[section].selector,
      { context: html },
    ).innerHTML
  }

  get form () {
    return this.renderer.form
  }

  get shouldShowLoadPrevious () {
    const anElementFromTheFirstPageExists = !!getElement(
      `[data-collection-filters-form-external-el="item"][data-page="1"]`,
    )
    if (anElementFromTheFirstPageExists) return false

    return this.currentPage > 1
  }

  get shouldShowLoadMore () {
    const anElementFromTheLastPageExists = getElement(
      `[data-collection-filters-form-external-el="item"][data-page="${this.lastPage}"]`,
    )
    if (anElementFromTheLastPageExists) return false

    return Number(this.currentPage) < this.lastPage
  }

  get shouldRerenderLoadPrevious () {
    return this.currentPage <= this.currentLowestPage
  }

  get shouldRerenderLoadMore () {
    return this.currentPage >= this.currentHighestPage
  }

  get currentPage () {
    const params = this.form.searchParams.current
    const page = params.get('page')

    return Number(page) || 1
  }

  get currentLowestPage () {
    const productGridItems = getElements(
      '[data-collection-filters-form-external-el="item"]',
    )

    return productGridItems.length > 0
      ? Number(productGridItems[0].dataset.page)
      : 1
  }

  get currentHighestPage () {
    const productGridItems = getElements(
      '[data-collection-filters-form-external-el="item"]',
    )

    return productGridItems.length > 0
      ? Number(productGridItems[productGridItems.length - 1].dataset.page)
      : 1
  }

  /**
   * @returns {number}
   */
  get lastPage () {
    return Number(
      this.form.externalEls['product-grid'].element.dataset.lastPage,
    )
  }
}

/**
 * @typedef {Object} FilterData
 *
 * @property {string} html
 * @property {string} url
 */

export class CollectionFiltersForm extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  static get requiredElements () {
    return ['facets']
  }

  static get requiredExternalElements () {
    return ['container', 'product-grid']
  }

  static isPaginating = false

  constructor () {
    super()

    /**
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: 'template-collection-product-grid',
        section: this.dataset.id,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {FilterData[]}
     * @public
     */
    this.filterData = []

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }

    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = new CollectionFiltersRenderer(this)
  }

  setupListeners () {
    super.setupListeners()

    this.onFacetRemovalClick = this.onFacetRemovalClick.bind(this)

    this.debouncedOnSubmit = debounce(e => this.onSubmit(e), 500)
    this.listeners.add(this, 'input', this.debouncedOnSubmit.bind(this))

    this.debouncedHandleResize = debounce(e => this.handleResize(e), 150)
    this.listeners.add(window, 'resize', this.debouncedHandleResize.bind(this))

    this.listeners.add(
      this,
      'AFTER_RANGE_SLIDER_CHANGE',
      this.onSubmit.bind(this),
    )

    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))
    this.listeners.add(
      this.els.facets.element,
      'keyup',
      this.onKeyUp.bind(this),
    )

    this.listeners.add(document, 'click', this.onPaginationClick.bind(this))
    this.listeners.add(document, 'click', this.onLinkClick.bind(this))
  }

  handleResize (e) {
    this.dispatchEvent(new CustomEvent('AFTER_RESIZE'))
  }

  /**
   * @param {PopStateEvent} e
   *
   * @returns {void}
   */
  onHistoryChange (e) {
    const searchParams = e.state
      ? e.state.searchParams
      : this.form.searchParams.initial

    if (!searchParams) return

    if (
      searchParams.toString() === this.form.searchParams.previous.toString()
    ) {
      return
    }

    this.form.renderPage({ searchParams, updateURLHash: false })
  }

  /**
   * @param {PointerEvent} e
   *
   * @returns {void}
   */
  onFacetRemovalClick (e) {
    e.preventDefault()

    const searchParams = e.currentTarget.href.includes('?')
      ? e.currentTarget.href.slice(e.currentTarget.href.indexOf('?') + 1)
      : ''

    this.log('onFacetRemovalClick', { searchParams })

    this.renderPage({ searchParams })
  }

  /**
   * @param {KeyboardEvent} e
   *
   * @returns {void}
   */
  onKeyUp (e) {
    if (e.code.toUpperCase() !== 'ESCAPE') return

    const openFacet = e.target.closest(`${this.els.facet.selector}[open]`)
    if (!openFacet) return

    const summary = getElement(`${this.els['facet-summary'].selector}`, {
      context: openFacet,
    })
    openFacet.removeAttribute('open')
    summary.setAttribute('aria-expanded', 'false')
    summary?.focus()
  }

  /**
   * @param {PointerEvent} e
   *
   * @returns {void}
   */
  onPaginationClick (e) {
    if (!e.target.closest(this.externalEls?.pagination?.selector)) return
    if (this.constructor.isPaginating) return

    e.preventDefault()

    const anchor = e.target.closest('a')
    this.log('onPaginationClick', { anchor })
    if (!anchor || anchor.getAttribute('href') === '' || anchor.disabled) return

    this.constructor.isPaginating = true

    const href = anchor.getAttribute('href')
    this.log('onPaginationClick', { href })
    // eslint-disable-next-line compat/compat
    const paginationSearchParams = new URLSearchParams(href.split('?')[1])
    const wantedPage = paginationSearchParams.get('page') ?? '1'
    // eslint-disable-next-line compat/compat
    const currentSearchParams = new URLSearchParams(
      this.searchParams.current.toString(),
    )
    currentSearchParams.set('page', wantedPage)

    this.renderPage({ searchParams: currentSearchParams.toString(), e })
  }

  /**
   * @param {PointerEvent} e
   */
  onLinkClick (e) {
    if (!e.target.closest('a')) return

    const listItem = e.target.closest('.prd-List_Item')
    if (!listItem) return

    const linkHasPageReference = listItem.hasAttribute('data-page')
    if (!linkHasPageReference) return true

    const searchParams = new URLSearchParams(window.location.search)
    const currentPage = searchParams.get('page') || '1'
    const productPage = listItem.dataset.page

    if (productPage !== currentPage) {
      searchParams.set('page', productPage)
      history.replaceState(
        {},
        '',
        `${window.location.pathname}?${searchParams}`,
      )
    }
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onSubmit (e) {
    e.preventDefault()

    if (e.target.hasAttribute('data-disable-form-submission')) {
      return
    }

    const formData = new FormData(this)
    // eslint-disable-next-line compat/compat
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({ searchParams, e })
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {InputEvent|PointerEvent} [e]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  renderPage ({ searchParams, e, updateURLHash = true } = {}) {
    this.log('renderPage', { searchParams, e, updateURLHash })

    // Here we're getting the size-related filters and adding the query string parameter which activates/shows items that are in-stock when selected.
    if (searchParams.includes('filter.v.option.size=')) {
      // Checking to make sure that parameter doesn't already exist so we're not adding it mulitple times
      if (!searchParams.includes('filter.v.availability=1')) {
        searchParams += '&filter.v.availability=1'
      }
    } else {
      // If the size query string/filters aren't selected, remove the in-stock query string parameter -- also reassigning searchParams so it has that removal included when it's passed to the following functionality
      searchParams = searchParams.replace('&filter.v.availability=1', '')
    }

    this.searchParams.previous = this.searchParams.current
    // eslint-disable-next-line compat/compat
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    this.sections.forEach(section => {
      const url = `${window.location.pathname}?section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      /**
       * @param {FilterData} element
       *
       * @returns {Boolean}
       */
      const filterDataUrl = element => element.url === url

      this.log('renderPageSections', { section, url })

      this.filterData.some(element => filterDataUrl(element))
        ? this.renderer.fromCache(filterDataUrl, e)
        : this.renderer.fromFetch(section.section, url, e)
    })

    if (updateURLHash) this.updateURLHash(this.searchParams.current)
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this._loading
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this._loading = val

    this.setAttribute('aria-busy', val.toString())

    this.externalEls?.container.element.setAttribute(
      'aria-busy',
      val.toString(),
    )

    if (val === false) {
      this.constructor.isPaginating = false
    }
  }
}

if (!customElements.get('collection-filters-form')) {
  customElements.define('collection-filters-form', CollectionFiltersForm, {
    extends: 'form',
  })
}

class FacetRemove extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.querySelector('a'), 'click', e => {
      e.preventDefault()

      this.form.onFacetRemovalClick(e)
    })
  }

  get form () {
    const selector = 'collection-filters-form, [is="collection-filters-form"]'

    return this.closest(selector) || document.querySelector(selector)
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove)
}

class FilterFacet extends window.BAO.CustomElement({
  SuperClass: HTMLDetailsElement,
}) {
  static get requiredElements () {
    return ['summary', 'content']
  }

  constructor () {
    super()

    /**
     * @type {Animation|null}
     * @public
     */
    this.animation = null

    /**
     * @type {Boolean}
     * @public
     */
    this.isClosing = false

    /**
     * @type {Boolean}
     * @public
     */
    this.isExpanding = false
  }

  setupListeners () {
    super.setupListeners()

    this.onClick = this.onClick.bind(this)

    this.listeners.add(this.els.summary.element, 'click', this.onClick)
  }

  /**
   * @param {PointerEvent} e
   *
   * @returns {void}
   */
  onClick (e) {
    e.preventDefault()

    if (this.isClosing || !this.open) {
      this.start()
    } else if (this.isExpanding || this.open) {
      this.close()
    }
  }

  start () {
    this.open = true

    window.requestAnimationFrame(() => this.expand())
  }

  close () {
    this.shrink()
  }

  shrink () {
    this.isClosing = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.els.content.element.animate(
      {
        opacity: [1, 0],
        transform: ['none', `translateY(0)`],
      },
      { duration: 0, easing: 'ease' },
    )

    this.animation.onfinish = () => this.onAnimationFinish(false)
    this.animation.oncancel = () => (this.isClosing = false)
  }

  expand () {
    this.isExpanding = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.els.content.element.animate(
      {
        opacity: [0, 1],
        transform: [`translateY(0)`, 'none'],
      },
      { duration: 0, easing: 'ease' },
    )

    this.animation.onfinish = () => this.onAnimationFinish(true)
    this.animation.oncancel = () => (this.isExpanding = false)
  }

  /**
   * @param {Boolean} open
   *
   * @returns {void}
   */
  onAnimationFinish (open) {
    this.open = open
    this.animation = null

    this.isClosing = false
    this.isExpanding = false
  }
}

if (!customElements.get('filter-facet')) {
  customElements.define('filter-facet', FilterFacet, { extends: 'details' })
}
